import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../apiBase';

interface User {
  email: string;
  role: string;
}

interface PineconeConfig {
  apiKey: string;
  environment: string;
  indexName: string;
}

interface GoogleCloudConfig {
  projectId: string;
  location: string;
  credentialsJson: string;
}

interface ShopifyConfig {
  shopUrl: string;
  accessToken: string;
  blogId: string;
}

export default function Account() {
  const [user, setUser] = useState<User | null>(null);
  const [shopifyShop, setShopifyShop] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [pineconeConfig, setPineconeConfig] = useState<PineconeConfig>({
    apiKey: '',
    environment: '',
    indexName: ''
  });
  const [googleCloudConfig, setGoogleCloudConfig] = useState<GoogleCloudConfig>({
    projectId: '',
    location: 'us-central1',
    credentialsJson: ''
  });
  const [googleCloudStatus, setGoogleCloudStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [shopifyConfig, setShopifyConfig] = useState<ShopifyConfig>({
    shopUrl: '',
    accessToken: '',
    blogId: ''
  });
  const [shopifyStatus, setShopifyStatus] = useState<{
    connected: boolean;
    shop?: string;
    message: string;
  } | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [availableIndexes, setAvailableIndexes] = useState<string[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  useEffect(() => {
    fetchAccountInfo();
    loadPineconeConfig();
    loadShopifyConfig();
    loadGoogleCloudConfig();
  }, []);

  const fetchAccountInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/check`, { withCredentials: true });
      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
      }

      // Get Shopify shop from backend
      const shopResponse = await axios.get(`${API_BASE}/api/account/shopify-info`, { withCredentials: true });
      if (shopResponse.data.success) {
        setShopifyShop(shopResponse.data.shop);
        setShopifyConfig(prev => ({ ...prev, shopUrl: shopResponse.data.shop || '' }));
      }
    } catch (error) {
      console.error('Error fetching account info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPineconeConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/load-pinecone-config`, { withCredentials: true });
      if (response.data.success && response.data.config) {
        setPineconeConfig(prev => ({
          ...prev,
          environment: response.data.config.environment || '',
          indexName: response.data.config.indexName || ''
        }));
      }
    } catch (error) {
      console.error('Failed to load Pinecone config:', error);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  const savePineconeConfig = async () => {
    if (!pineconeConfig.apiKey || !pineconeConfig.indexName) {
      setConnectionStatus({
        success: false,
        message: 'API Key and Index Name are required to save configuration'
      });
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/save-pinecone-config`, {
        apiKey: pineconeConfig.apiKey,
        environment: pineconeConfig.environment,
        indexName: pineconeConfig.indexName
      }, { withCredentials: true });

      setConnectionStatus({
        success: true,
        message: response.data.message || 'Configuration saved successfully'
      });
    } catch (error: any) {
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || 'Failed to save configuration'
      });
    }
  };

  const loadShopifyConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/shopify/status`, { withCredentials: true });
      setShopifyStatus({
        connected: response.data.connected,
        shop: response.data.shop,
        message: response.data.connected ? 'Shopify store connected' : 'No Shopify store connected'
      });
    } catch (error) {
      console.error('Failed to load Shopify config:', error);
      setShopifyStatus({
        connected: false,
        message: 'Failed to check Shopify connection'
      });
    }
  };

  const loadGoogleCloudConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/load-google-cloud-config`, { withCredentials: true });
      if (response.data.success && response.data.config) {
        setGoogleCloudConfig(prev => ({
          ...prev,
          projectId: response.data.config.projectId || '',
          location: response.data.config.location || 'us-central1'
        }));
        setGoogleCloudStatus({
          success: true,
          message: response.data.config.hasCredentials ? 'Google Cloud configured' : 'Project ID set, credentials needed'
        });
      }
    } catch (error) {
      console.error('Failed to load Google Cloud config:', error);
    }
  };

  const saveGoogleCloudConfig = async () => {
    if (!googleCloudConfig.projectId) {
      setGoogleCloudStatus({
        success: false,
        message: 'Project ID is required'
      });
      return;
    }

    try {
      const response = await axios.post(`${API_BASE}/api/save-google-cloud-config`, {
        projectId: googleCloudConfig.projectId,
        location: googleCloudConfig.location,
        credentialsJson: googleCloudConfig.credentialsJson || null
      }, { withCredentials: true });

      setGoogleCloudStatus({
        success: true,
        message: response.data.message || 'Configuration saved successfully'
      });
    } catch (error: any) {
      setGoogleCloudStatus({
        success: false,
        message: error.response?.data?.error || 'Failed to save configuration'
      });
    }
  };


  const connectShopifyStore = () => {
    // Redirect to Shopify OAuth flow
    const url = `${API_BASE}/shopify/auth?shop=${shopifyConfig.shopUrl}`;

    // Break out of iframe if embedded
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
    } else {
      window.location.href = url;
    }
  };

  const testPineconeConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    setAvailableIndexes([]);

    try {
      const response = await axios.post(`${API_BASE}/api/test-pinecone-connection`, {
        apiKey: pineconeConfig.apiKey,
        environment: pineconeConfig.environment,
        indexName: pineconeConfig.indexName
      }, { withCredentials: true });

      setConnectionStatus({
        success: response.data.success,
        message: response.data.message || response.data.error
      });

      if (response.data.availableIndexes) {
        setAvailableIndexes(response.data.availableIndexes);
      }
    } catch (error: any) {
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || 'Connection test failed'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
          <p className="mt-2 text-gray-600">Loading account info...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">👤 Account</h1>
        <p className="text-gray-600">Manage your connected store and app preferences.</p>
      </div>

      {/* Account Settings Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Account Settings</h2>
        <p className="text-gray-600 mb-6">Manage your connected store and app preferences.</p>

        {/* Logged in Email */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logged in as
          </label>
          <div className="px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg">
            <div className="flex items-center">
              <span className="text-gray-900 font-medium">{user?.email || 'Not logged in'}</span>
              <span className="ml-3 px-2 py-1 bg-pink-100 text-pink-800 text-xs font-medium rounded">
                {user?.role || 'User'}
              </span>
            </div>
          </div>
        </div>

        {/* Connected Shop */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Connected Shop
          </label>
          <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                {shopifyShop ? (
                  <>
                    <p className="text-gray-900 font-medium">{shopifyShop}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      🟢 Connected via Shopify API
                    </p>
                  </>
                ) : (
                  <p className="text-gray-600">No shop connected</p>
                )}
              </div>
              {shopifyShop && (
                <a
                  href={`https://${shopifyShop}/admin`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Open Admin →
                </a>
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          OAuth is handled securely; no tokens are shown here. Use Log out in the header to disconnect.
        </p>
      </div>

      {/* Service Status Overview */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">🔧 Service Status</h2>
        <p className="text-gray-600 mb-4">
          Overview of your connected services and integrations.
        </p>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🤖</span>
              <div>
                <p className="font-medium text-gray-900">Gemini AI</p>
                <p className="text-sm text-gray-600">Content generation & image analysis</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded">
              ✓ System
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">☁️</span>
              <div>
                <p className="font-medium text-gray-900">Google Cloud / Vertex AI</p>
                <p className="text-sm text-gray-600">Text embeddings & vector search</p>
              </div>
            </div>
            <span className={`px-3 py-1 text-sm font-medium rounded ${googleCloudStatus?.success ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {googleCloudStatus?.success ? '✓ Connected' : '○ Not Configured'}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <p className="font-medium text-gray-900">Pinecone</p>
                <p className="text-sm text-gray-600">Vector database for product search</p>
              </div>
            </div>
            <span className={`px-3 py-1 text-sm font-medium rounded ${connectionStatus?.success ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {connectionStatus?.success ? '✓ Connected' : '○ Not Configured'}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🛍️</span>
              <div>
                <p className="font-medium text-gray-900">Shopify</p>
                <p className="text-sm text-gray-600">Product data & blog publishing</p>
              </div>
            </div>
            <span className={`px-3 py-1 text-sm font-medium rounded ${shopifyStatus?.connected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {shopifyStatus?.connected ? '✓ Connected' : '○ Not Connected'}
            </span>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🖼️</span>
              <div>
                <p className="font-medium text-gray-900">Image Generation</p>
                <p className="text-sm text-gray-600">AI-powered image creation</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded">
              ✓ System
            </span>
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>🔒 Security:</strong> Your personal API keys and credentials are encrypted and stored securely.
            Configure your services above to unlock full functionality.
          </p>
        </div>
      </div>

      {/* Shopify Store Configuration */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">🛍️ Shopify Store Connection</h2>
        <p className="text-gray-600 mb-4">
          Install this app in your Shopify store to automatically publish generated blog posts and upload images.
        </p>

        {shopifyStatus && (
          <div className={`p-3 rounded-lg mb-4 ${shopifyStatus.connected ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className={`text-sm ${shopifyStatus.connected ? 'text-green-800' : 'text-red-800'}`}>
              {shopifyStatus.connected ? '✅' : '❌'} {shopifyStatus.message}
              {shopifyStatus.connected && shopifyStatus.shop && (
                <span className="block mt-1 text-xs">Store: {shopifyStatus.shop}</span>
              )}
            </p>
          </div>
        )}

        {!shopifyStatus?.connected && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Shopify Store URL
              </label>
              <input
                type="text"
                value={shopifyConfig.shopUrl}
                onChange={(e) => setShopifyConfig(prev => ({ ...prev, shopUrl: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., mystore.myshopify.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter your Shopify store URL to install the Catalog Muse app
              </p>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={connectShopifyStore}
                disabled={!shopifyConfig.shopUrl.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                🚀 Install in Shopify Store
              </button>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>🔄 Automatic Setup:</strong> Clicking the button will redirect you to Shopify to install the Catalog Muse app.
                Once installed, you'll be automatically connected with full access to publish content.
              </p>
            </div>
          </div>
        )}

        {shopifyStatus?.connected && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>✅ Connected!</strong> You can now generate and publish blog posts directly to your Shopify store.
            </p>
          </div>
        )}

        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>🔐 Security:</strong> The app uses Shopify's official OAuth flow. No manual API keys needed - authentication is handled securely by Shopify.
          </p>
        </div>
      </div>

      {/* Pinecone Configuration */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">🌲 Pinecone Configuration</h2>
        <p className="text-gray-600 mb-4">
          Connect your Pinecone vector database for AI-powered product recommendations.
          {isLoadingConfig && <span className="ml-2 text-sm text-gray-500">Loading configuration...</span>}
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API Key
            </label>
            <input
              type="password"
              value={pineconeConfig.apiKey}
              onChange={(e) => setPineconeConfig(prev => ({ ...prev, apiKey: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              placeholder="Enter your Pinecone API key"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Environment
            </label>
            <input
              type="text"
              value={pineconeConfig.environment}
              onChange={(e) => setPineconeConfig(prev => ({ ...prev, environment: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              placeholder="e.g., us-east-1, aped-4627-b74a"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Index Name
            </label>
            {availableIndexes.length > 0 ? (
              <select
                value={pineconeConfig.indexName}
                onChange={(e) => setPineconeConfig(prev => ({ ...prev, indexName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                <option value="">Select an index...</option>
                {availableIndexes.map(index => (
                  <option key={index} value={index}>{index}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={pineconeConfig.indexName}
                onChange={(e) => setPineconeConfig(prev => ({ ...prev, indexName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., shopify-products"
              />
            )}
            {availableIndexes.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">Select from your available indexes</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={testPineconeConnection}
              disabled={testingConnection || !pineconeConfig.apiKey}
              className="px-4 py-2 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white rounded-lg hover:from-fuchsia-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {testingConnection ? '🔍 Testing...' : '🔍 Test Connection'}
            </button>

            <button
              onClick={savePineconeConfig}
              disabled={!pineconeConfig.apiKey || !pineconeConfig.indexName}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              💾 Save Configuration
            </button>
          </div>

          {connectionStatus && (
            <div className={`p-3 rounded-lg ${connectionStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm ${connectionStatus.success ? 'text-green-800' : 'text-red-800'}`}>
                {connectionStatus.success ? '✅' : '❌'} {connectionStatus.message}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Google Cloud / Vertex AI Configuration */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">☁️ Google Cloud / Vertex AI Configuration</h2>
        <p className="text-gray-600 mb-4">
          Connect your Google Cloud account for Vertex AI embeddings. Required for product search and recommendations.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={googleCloudConfig.projectId}
              onChange={(e) => setGoogleCloudConfig(prev => ({ ...prev, projectId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              placeholder="e.g., my-project-12345"
            />
            <p className="text-xs text-gray-500 mt-1">Your Google Cloud Project ID</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <select
              value={googleCloudConfig.location}
              onChange={(e) => setGoogleCloudConfig(prev => ({ ...prev, location: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="us-central1">us-central1 (Iowa)</option>
              <option value="us-east1">us-east1 (South Carolina)</option>
              <option value="us-east4">us-east4 (Virginia)</option>
              <option value="us-west1">us-west1 (Oregon)</option>
              <option value="us-west4">us-west4 (Las Vegas)</option>
              <option value="europe-west1">europe-west1 (Belgium)</option>
              <option value="europe-west4">europe-west4 (Netherlands)</option>
              <option value="asia-east1">asia-east1 (Taiwan)</option>
              <option value="asia-southeast1">asia-southeast1 (Singapore)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Region where your Vertex AI resources are located</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Service Account JSON (Optional)
            </label>
            <textarea
              value={googleCloudConfig.credentialsJson}
              onChange={(e) => setGoogleCloudConfig(prev => ({ ...prev, credentialsJson: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 font-mono text-xs"
              placeholder='Paste your service account JSON here, or leave blank to use Application Default Credentials'
              rows={6}
            />
            <p className="text-xs text-gray-500 mt-1">
              Paste your service account JSON key file contents. If left blank, the system will use Application Default Credentials (ADC).
            </p>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900 mb-2">
              <strong>📋 How to get your credentials:</strong>
            </p>
            <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
              <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
              <li>Select or create a project</li>
              <li>Enable the Vertex AI API</li>
              <li>Create a service account with Vertex AI User role</li>
              <li>Download the JSON key file and paste it above (or use ADC)</li>
            </ol>
          </div>

          <button
            onClick={saveGoogleCloudConfig}
            disabled={!googleCloudConfig.projectId}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            💾 Save Google Cloud Configuration
          </button>

          {googleCloudStatus && (
            <div className={`p-3 rounded-lg ${googleCloudStatus.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <p className={`text-sm ${googleCloudStatus.success ? 'text-green-800' : 'text-red-800'}`}>
                {googleCloudStatus.success ? '✅' : '❌'} {googleCloudStatus.message}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
